import React, { useState } from 'react';
import {
  Alert,
  AlertIcon,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  Input,
  Link,
  Stack,
  Text,
} from '@chakra-ui/react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { apiRegister } from '../api/client';
import PortalAuthShell from './PortalAuthShell';

export default function RegisterPage() {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    setError('');
    const trimmedFullName = fullName.trim();
    const trimmedPhone = phone.trim() || undefined;
    setSubmitting(true);

    try {
      const res: any = await apiRegister(trimmedFullName, email, password, trimmedPhone);
      if (res.success) {
        navigate('/member/articles', { replace: true });
        return;
      }

      let details = '';

      if (typeof res.message === 'string' && res.message.trim()) {
        details = res.message.trim();
      } else if (typeof res.error === 'string' && res.error.trim()) {
        details = res.error.trim();
      } else if (Array.isArray(res.errors) && res.errors.length > 0) {
        details = res.errors.join(', ');
      }

      setError(details ? `Registration failed: ${details}` : 'Registration failed');
    } catch (error: any) {
      setError(error?.message || 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PortalAuthShell
      title="Create your account"
      subtitle="Register to access your portal."
    >
      <Stack as="form" spacing={5} onSubmit={handleSubmit}>
        <Stack spacing={2}>
          <Heading size="lg">Register</Heading>
          <Text color="gray.600">Create your account to access member content.</Text>
        </Stack>

        {error ? (
          <Alert status="error" borderRadius="lg">
            <AlertIcon />
            {error}
          </Alert>
        ) : null}

        <FormControl isRequired>
          <FormLabel>Full Name</FormLabel>
          <Input
            type="text"
            placeholder="Enter your full name"
            value={fullName}
            onChange={e => setFullName(e.target.value)}
            size="lg"
            isDisabled={submitting}
          />
        </FormControl>

        <FormControl isRequired>
          <FormLabel>Email</FormLabel>
          <Input
            type="email"
            placeholder="name@example.com"
            value={email}
            onChange={e => setEmail(e.target.value)}
            size="lg"
            isDisabled={submitting}
          />
        </FormControl>

        <FormControl>
          <FormLabel>Phone</FormLabel>
          <Input
            type="tel"
            placeholder="Mobile number"
            value={phone}
            onChange={e => setPhone(e.target.value)}
            size="lg"
            isDisabled={submitting}
          />
          <FormHelperText>Optional. Used for account contact only.</FormHelperText>
        </FormControl>

        <FormControl isRequired>
          <FormLabel>Password</FormLabel>
          <Input
            type="password"
            placeholder="Minimum 6 characters"
            value={password}
            onChange={e => setPassword(e.target.value)}
            size="lg"
            isDisabled={submitting}
          />
          <FormHelperText>Password should be at least 6 characters.</FormHelperText>
        </FormControl>

        <Button type="submit" colorScheme="blue" size="lg" isLoading={submitting} loadingText="Creating account" isDisabled={submitting}>
          Create account
        </Button>

        <Text color="gray.600">
          Already have an account?{' '}
          <Link as={RouterLink} to="/member/login" color="blue.600" fontWeight="600">
            Login
          </Link>
        </Text>
      </Stack>
    </PortalAuthShell>
  );
}
